import React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material';

interface LoadingSpinnerProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'spinner' | 'skeleton' | 'fullscreen';
  skeletonRows?: number;
  skeletonHeight?: number;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = 'Chargement...',
  size = 'medium',
  variant = 'spinner',
  skeletonRows = 3,
  skeletonHeight = 60,
}) => {
  const getSize = () => {
    switch (size) {
      case 'small':
        return 24;
      case 'large':
        return 48;
      default:
        return 32;
    }
  };

  if (variant === 'fullscreen') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <CircularProgress size={getSize()} />
        {message && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: 2, textAlign: 'center' }}
          >
            {message}
          </Typography>
        )}
      </Box>
    );
  }

  if (variant === 'skeleton') {
    return (
      <Box sx={{ p: 2 }}>
        {Array.from({ length: skeletonRows }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            height={skeletonHeight}
            sx={{ mb: 2, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <CircularProgress size={getSize()} />
      {message && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, textAlign: 'center' }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

// Table loading skeleton
export const TableLoadingSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 6,
}) => (
  <Box sx={{ p: 2 }}>
    {/* Header skeleton */}
    <Box sx={{ display: 'flex', mb: 2 }}>
      {Array.from({ length: columns }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          height={40}
          sx={{ flex: 1, mr: 1, borderRadius: 1 }}
        />
      ))}
    </Box>
    
    {/* Row skeletons */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box key={rowIndex} sx={{ display: 'flex', mb: 1 }}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton
            key={colIndex}
            variant="rectangular"
            height={50}
            sx={{ flex: 1, mr: 1, borderRadius: 1 }}
          />
        ))}
      </Box>
    ))}
  </Box>
);

// Card loading skeleton
export const CardLoadingSkeleton: React.FC<{ cards?: number }> = ({ cards = 4 }) => (
  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
    {Array.from({ length: cards }).map((_, index) => (
      <Card key={index} sx={{ flex: 1 }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={24} />
          <Skeleton variant="rectangular" height={60} sx={{ mt: 2, borderRadius: 1 }} />
        </CardContent>
      </Card>
    ))}
  </Box>
);

export default LoadingSpinner; 